var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var SnippetState_1;
/** @file This file contains the store for the current logged in user. */
import moment from 'moment';
import { Left, Maybe, Nothing, Right } from 'purify-ts';
import { getStoreBuilder } from 'vuex-typex';
import * as apiModels from '@codegrade/api';
import { array, datetime, eitherAsync, immutable, object, promise, sort } from '@codegrade/utils';
import client, { Client, unauthorizedClient } from '@/client';
import * as models from '@/models';
import { LoggedInUser } from '@/models';
import { getDefaultGlobalPermissions, GlobalPermissions as GPerm, } from '@/permissions';
import * as utils from '@/utils';
import { SiteSettingsStore } from './siteSettings';
import { UIPrefsStore } from './ui_prefs';
const storeBuilder = getStoreBuilder();
/**
 * The state of the snippets of a user.
 */
let SnippetState = SnippetState_1 = class SnippetState {
    data;
    lastLoaded;
    /**
     * @param data A mapping between snippet key and the snippet.
     * @param lastLoaded Maybe the last time the snippets were loaded. If this
     * is `Nothing` the snippets have not been loaded yet.
     */
    constructor(data = {}, lastLoaded = Maybe.empty()) {
        this.data = data;
        this.lastLoaded = lastLoaded;
        Object.freeze(this.data);
    }
    /**
     * Add a new snippet to this state.
     *
     * @param snippet The snippet to add.
     * @returns A fresh snippet state.
     */
    addSnippet(snippet) {
        const data = { ...this.data };
        const oldSnippet = Object.values(data).find(s => s.id === snippet.id);
        if (oldSnippet != null) {
            utils.vueDelete(data, oldSnippet.key);
        }
        return new SnippetState_1({ ...data, [snippet.key]: snippet }, this.lastLoaded);
    }
    /**
     * Remove a snippet from this state.
     *
     * @param toRemove The snippet to remove.
     * @returns A fresh snippet state without the given snippet.
     */
    deleteSnippet(toRemove) {
        return new SnippetState_1(object.filterObject(this.data, snippet => snippet.key !== toRemove.key), this.lastLoaded);
    }
};
SnippetState = SnippetState_1 = __decorate([
    immutable.immutable
], SnippetState);
/**
 * Set the current user in sentry.
 *
 * @param user The user to set as the current user in Sentry.
 */
function setUser(user) {
    utils.sentry.withSentry(Sentry => {
        Sentry.setUser(user);
    });
}
/**
 * Create the initial state for the `UserStore`.
 *
 * @returns A fresh initial state.
 */
const makeInitialState = () => ({
    loggedInUser: Nothing,
    snippets: Nothing,
    permissions: {},
    permissionsLoaders: {},
});
const moduleBuilder = storeBuilder.module('user', makeInitialState());
const manageSitePermissions = Object.freeze(Object.values(GPerm).filter(perm => perm.manageAdmin));
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var UserStore;
(function (UserStore) {
    /**
     * A getter to check if we have a logged in user.
     */
    UserStore.loggedIn = moduleBuilder.read(state => state.loggedInUser.isJust(), 'loggedIn');
    /**
     * A getter to retrieve the user id of the currently logged in user. Returns
     * 0 if there is no logged in user.
     */
    UserStore.id = moduleBuilder.read(state => state.loggedInUser.mapOrDefault(u => u.id, 0), 'id');
    /**
     * A getter for the snippets of the currently logged in user.
     */
    UserStore.snippets = moduleBuilder.read(state => state.snippets.mapOrDefault(s => s.data, {}), 'snippets');
    /**
     * Get the currently logged in user.
     *
     * @param state The state of the user store.
     * @returns The currenlty logged in user if it exists and the user can be found.
     */
    function getUser(state) {
        return state.loggedInUser.chainNullable(({ id: userId }) => models.User.findUserById(userId));
    }
    /**
     * The currently logged in user as a `models.LoggedInUser` object.
     */
    UserStore.loggedInUser = moduleBuilder.read((state) => state.loggedInUser, 'loggedInUser');
    /**
     * A getter for the currently logged in user as a `models.User` object.
     *
     * Also see `getUser` for more information.
     */
    UserStore.currentUser = moduleBuilder.read(getUser, 'currentUser');
    /**
     * A getter to retrieve maybe the tenant id of the currently logged in user.
     */
    UserStore.tenantId = moduleBuilder.read(() => UserStore.loggedInUser().chain(u => u.tenantId), 'tenantId');
    /**
     * Retrieve an attribute of the current user.
     *
     * @param attr The attribute to retireve of the current user.
     * @returns A function to retrieve the give attribute of the current user,
     * or '' if there is no current user at the time of calling the returned
     * function.
     */
    function userAttr(attr) {
        return () => UserStore.currentUser().mapOrDefault(u => u[attr], '');
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.username = moduleBuilder.read(userAttr('username'), 'username');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.canSeeHidden = moduleBuilder.read(state => state.loggedInUser.mapOrDefault(u => u.canSeeHidden, false), 'canSeeHidden');
    const getGlobalPermissions = moduleBuilder.read(state => state.loggedInUser.map(u => u.permissions).orDefaultLazy(getDefaultGlobalPermissions), 'getGlobalPermissions');
    /**
     * Get the permission mapping for the current user in the given tenant.
     *
     * When the value of a certain permission in this mapping is `false` that does NOT mean
     * that the user does not have the permission, they might have the global permission!
     */
    const getTenantPermissions = moduleBuilder.read(state => 
    /**
     * The actual.
     *
     * @param maybeTenantId The tenant id in which to retrieve the permission.
     * @returns If the user has the given permission.
     */
    (maybeTenantId) => maybeTenantId.chain(object.getItem(state.permissions)).join(), 'getTenantPermissions');
    /** Returns function to check if a user has a specific global permission. */
    UserStore.hasGlobalPermission = moduleBuilder.read(() => {
        const perms = getGlobalPermissions();
        return (perm) => perms[perm.value] ?? false;
    }, 'hasGlobalPermission');
    /** Returns function that checks if a user has a tenant permission. */
    UserStore.hasTenantPermission = moduleBuilder.read(() => {
        const getTenantPerms = getTenantPermissions();
        const hasGlobalPerm = UserStore.hasGlobalPermission();
        /**
         * Check if a user has a global permission in a tenant.
         *
         * @param perm The permission to check for.
         * @param maybeTenantId Maybe the tenant id in which to check for the permission. If not
         * given global permissions will be checked.
         * @returns If the current user has the given permission in the given tenant.
         */
        return (perm, maybeTenantId) => getTenantPerms(maybeTenantId)
            .chain(permMap => Maybe.fromFalsy(permMap[perm.value]))
            .orDefaultLazy(() => hasGlobalPerm(perm));
    }, 'hasTenantPermission');
    /** Returns function that check the user is an admin in the specified tenant.  */
    UserStore.isAdminIn = moduleBuilder.read(() => {
        const getSetting = SiteSettingsStore.getSetting();
        const hasTenantPerm = UserStore.hasTenantPermission();
        return (maybeTenantId) => getSetting('IS_ADMIN_PERMISSION_ENABLED', maybeTenantId) &&
            hasTenantPerm(GPerm.is_admin, maybeTenantId);
    }, 'isAdminIn');
    /**
     * Check whether the user can manage site via permission map.
     *
     * @param perms Global permission map.
     * @returns Whether the user has some manage site permissions.
     */
    function hasManagePerm(perms) {
        return manageSitePermissions.some(perm => perms[perm.value]);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.canManageSite = moduleBuilder.read(() => hasManagePerm(getGlobalPermissions()), 'canManageSite');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.canManageTenant = moduleBuilder.read(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    () => (maybeTenantId) => getTenantPermissions()(maybeTenantId)
        .chain(perms => Maybe.fromFalsy(hasManagePerm(perms)))
        .orDefaultLazy(UserStore.canManageSite), 'canManageTenant');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.hasCoursePermission = moduleBuilder.read(state => state.loggedInUser.mapOrDefault(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    user => (permission, course) => user.hasCoursePermission(permission, course), () => false), 'hasCoursePermission');
    /** Getter for a function that retrieves snippets by their id. */
    UserStore.findSnippetsByPrefix = moduleBuilder.read(state => {
        const values = state.snippets.mapOrDefault(({ data }) => sort.sortBy(Object.values(data), snip => [snip.key]), []);
        return (prefix) => values.filter(({ key }) => key.startsWith(prefix));
    }, 'findSnippetsByPrefix');
    /**
     * The current session of the user.
     *
     * Only use this when you know what you are doing, as you might accidentally leak the session of
     * the user this way.
     */
    UserStore.dangerousSession = moduleBuilder.read(state => state.loggedInUser
        .map(u => u.session)
        .filter(session => {
        const expiresAt = session.expiresAt.extract();
        if (expiresAt == null) {
            return true;
        }
        return expiresAt.isAfter(moment.utc().subtract(1, 'minute'));
    }), 'dangerousSession');
    const setSnippets = moduleBuilder.commit((state, newSnippets) => {
        utils.vueSet(state, 'snippets', Maybe.of(new SnippetState(newSnippets, Maybe.of(new datetime.RelativeTime()))));
    }, 'setSnippets');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.addSnippet = moduleBuilder.commit((state, payload) => {
        const curSnippets = state.snippets.orDefaultLazy(() => new SnippetState());
        utils.vueSet(state, 'snippets', Maybe.of(curSnippets.addSnippet(payload)));
    }, 'addSnippet');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.updateSnippet = moduleBuilder.commit((_, payload) => UserStore.addSnippet(payload), 'updateSnippet');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.deleteSnippet = moduleBuilder.commit((state, payload) => {
        utils.vueSet(state, 'snippets', state.snippets.map(s => s.deleteSnippet(payload)));
    }, 'deleteSnippet');
    const commitLogout = moduleBuilder.commit(state => {
        Object.entries(makeInitialState()).forEach(([key, value]) => {
            utils.vueSet(state, key, value);
        });
    }, 'commitLogout');
    /** Login a user. */
    const commitLogin = moduleBuilder.commit((state, data) => {
        utils.vueSet(state, 'loggedInUser', Maybe.of(data.user));
        setUser({
            id: `${data.user.id}`,
            username: data.user.username,
        });
    }, 'commitLogin');
    const commitTenantPermissions = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => utils.vueSet(state.permissions, payload.tenantId, payload.perms), 'commitTenantPermissions');
    /** Set a permission for a user. */
    UserStore.setPermission = moduleBuilder.commit((state, payload) => {
        const { permission, value } = payload;
        payload.tenantId.caseOf({
            Nothing: () => {
                UserStore.loggedInUser().ifJust(u => commitLogin(u.setPermission(permission, value)));
            },
            Just: tId => {
                Maybe.fromNullable(state.permissions[tId])
                    .join()
                    .ifJust(perms => {
                    const newPerms = Object.freeze({ ...perms, [permission]: value });
                    utils.vueSet(state.permissions, tId, Maybe.of(newPerms));
                });
            },
        });
    }, 'setPermission');
    const innerLogin = moduleBuilder.dispatch((_, { user: userData, sessions, loginMethod, }) => {
        const user = new LoggedInUser(userData, {
            canSeeHidden: userData.hidden,
            permissions: userData.permissions ?? getDefaultGlobalPermissions(),
            sessions,
            loginMethod,
            email: userData.email,
            payments: userData.payments,
            usedCoupons: userData.usedCoupons,
        });
        commitLogin({ user });
        utils.loadBackgroundApis(UIPrefsStore.loadUIPreference({ preference: 'theme' }), UserStore.loadPermissions({ tenantId: user.tenantId }));
        return user;
    }, 'innerLogin');
    /** Login a user. */
    UserStore.login = moduleBuilder.dispatch((_, { data, loginMethod, }) => innerLogin({
        user: data.user,
        sessions: [
            {
                restrictions: data.restrictions,
                token: data.accessToken,
                expiresAt: Maybe.of(data.expiresAt),
            },
        ],
        loginMethod: Maybe.of(loginMethod),
    }).then(user => eitherAsync.toPromise(utils.signal.AFTER_USER_CHANGED.send(Maybe.of(user)))), 'login');
    /** Logout the currently logged in user. */
    UserStore.logout = moduleBuilder.dispatch(async (_, { revoke, signals = true, } = { revoke: true }) => {
        const cur = UserStore.loggedInUser().extract();
        commitLogout();
        if (cur != null && revoke) {
            await Promise.all(cur.sessions.map(({ token }) => unauthorizedClient.p.user.logout({ requestBody: { token } })));
        }
        if (signals) {
            await eitherAsync.toPromise(utils.signal.AFTER_USER_CHANGED.send(Maybe.empty()));
        }
    }, 'logout');
    /** Do a login using the username, tenant id, and password of a user. */
    UserStore.loginWithUsername = moduleBuilder.dispatch((_, { data, loginMethod, }) => client.user.login({ requestBody: data, withPermissions: true }).map(response => response.addCallbacks({
        onSuccess: async ({ cgResult }) => {
            const wasLoggedIn = UserStore.loggedIn();
            if (wasLoggedIn) {
                await UserStore.logout({ revoke: true, signals: false });
            }
            const user = await innerLogin({
                user: cgResult.user,
                sessions: [
                    {
                        restrictions: cgResult.restrictions,
                        token: cgResult.accessToken,
                        expiresAt: Maybe.of(cgResult.expiresAt),
                    },
                ],
                loginMethod: Maybe.of(loginMethod),
            });
            await eitherAsync.toPromise(utils.signal.AFTER_USER_CHANGED.send(Maybe.of(user)));
        },
    })), 'loginWithUsername');
    /** Returns function that refresh snippets. */
    UserStore.refreshSnippets = moduleBuilder.dispatch(async () => {
        const result = await eitherAsync.toPromise(client.snippet
            .getAll()
            .map(({ body }) => object.mapToObject(body, snip => [snip.key, snip], {})));
        setSnippets(result);
    }, 'refreshSnippets');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.maybeRefreshSnippets = moduleBuilder.dispatch(ctx => {
        if (ctx.state.snippets
            .chain(s => s.lastLoaded)
            .map(lastLoaded => lastLoaded.since().asMinutes())
            .map(diff => diff > 5 && Math.random() < ((diff - 5) / (60 * 24)) ** 2)
            .orDefault(true)) {
            return UserStore.refreshSnippets();
        }
        return Promise.resolve();
    }, 'maybeRefreshSnippets');
    /**
     * Check that the given session is valid.
     *
     * @param sessions The sessions to check for validity.
     * @param loginMethod The method the user used to log in.
     * @param maxTries The maximum amount of times to retry 500 errors.
     * @returns A Promise that will resolve to the logged in user if the session is valid.
     */
    async function verifyLoginCheck(sessions, loginMethod, maxTries) {
        const tmpClient = new Client({ token: Maybe.of(array.last(sessions).token) });
        return promise.retry(async () => {
            const { body } = await eitherAsync.toPromise(tmpClient.user.get({ extended: true, withPermissions: true }));
            if (!(body instanceof apiModels.ExtendedUser)) {
                throw new Error('Got wrong data from API');
            }
            return await innerLogin({
                user: body,
                sessions,
                loginMethod,
            });
        }, {
            shouldRetry: (tryN, err) => 
            // We also want non API errors, so we can't use
            // ``handleApiError``.
            tryN < maxTries &&
                err instanceof apiModels.ApiResultError &&
                err.response.status / 100 === 5,
        });
    }
    /**
     * A getter to retrieve the current state of the student view.
     *
     * If the user is not logged in, the state is 'disabled'.
     * If the user has no removed course permissions, the state is 'disabled'.
     * Otherwise, the state is 'enabled' and indicates whether the user can leave the session.
     * We can only leave if we have a base session token to fall back to.
     *
     * @returns An object representing the state of the student view.
     */
    UserStore.studentView = moduleBuilder.read(() => {
        const user = UserStore.loggedInUser().extract();
        if (user == null) {
            return Object.freeze({ tag: 'disabled' });
        }
        return user.getStudentViewState();
    }, 'studentView');
    /** Verify that the login is working. */
    UserStore.verifyLogin = moduleBuilder.dispatch(
    /**
     * The function to perform this action.
     *
     * @param ctx The context to perform the action with.
     * @param opts The options to use.
     * @returns A promise that will resolve if the login is correct.
     */
    async (ctx, opts) => await ctx.state.loggedInUser.caseOf({
        Nothing: () => Promise.reject(Error('Not logged in')),
        Just: async (curLoggedIn) => {
            const tmpClient = new Client({ token: Maybe.of(curLoggedIn.session.token) });
            const [user] = await Promise.all([
                verifyLoginCheck(curLoggedIn.sessions, curLoggedIn.loginMethod, 10),
                eitherAsync.toPromise(SiteSettingsStore.loadSettings({ tenantId: curLoggedIn.tenantId })),
                eitherAsync.toPromise(UserStore.loadPermissions({
                    tenantId: curLoggedIn.tenantId,
                    client: tmpClient,
                })),
                eitherAsync.toPromise(UIPrefsStore.loadUIPreference({
                    preference: 'theme',
                    client: tmpClient,
                })),
            ]);
            if (opts.sendSignal) {
                await eitherAsync.toPromise(utils.signal.AFTER_USER_CHANGED.send(Maybe.of(user)));
            }
            return user;
        },
    }), 'verifyLogin');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.updateUserInfo = moduleBuilder.dispatch((ctx, data) => {
        const curUser = ctx.state.loggedInUser.unsafeCoerce();
        return client.user
            .patch({
            userId: curUser.id,
            requestBody: {
                name: data.name,
                email: data.email,
                oldPassword: data.oldPw,
                newPassword: data.newPw,
            },
        })
            .chain(response => {
            const user = curUser.updateFromApi(response.body);
            commitLogin({ user });
            return ctx.dispatch('users/addOrUpdateUser', { user }, { root: true }).then(() => Right(response), (err) => Left(err));
        });
    }, 'updateUserInfo');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.updateAccessToken = moduleBuilder.dispatch(async (_, { newSession, loginMethod, revokeOldToken, }) => {
        // When opening the page in a new tab with the "New tab" button in
        // the sidebar, we update the store to the same token, so in that
        // case we should NOT revoke the old token.
        await UserStore.logout({ revoke: revokeOldToken });
        const user = await verifyLoginCheck([newSession], Maybe.of(loginMethod), 1);
        await eitherAsync.toPromise(utils.signal.AFTER_USER_CHANGED.send(Maybe.of(user)));
    }, 'updateAccessToken');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.putTransactions = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (ctx, data) => {
        const curUser = UserStore.loggedInUser().unsafeCoerce();
        const newUser = curUser.putTransactions(data.transactions);
        commitLogin({ user: newUser });
        return ctx.dispatch('users/addOrUpdateUser', { user: newUser }, { root: true });
    }, 'putTransactions');
    /** Stores the list of coupons used by the current user. */
    UserStore.putUsedCoupons = moduleBuilder.dispatch((ctx, data) => {
        const curUser = UserStore.loggedInUser().unsafeCoerce();
        const newUser = curUser.putUsedCoupons(data.usedCoupons);
        commitLogin({ user: newUser });
        return ctx.dispatch('users/addOrUpdateUser', { user: newUser }, { root: true });
    }, 'putUsedCoupons');
    /** Add restrictions to the currently logged in user. */
    UserStore.addRestrictions = moduleBuilder.dispatch(
    /**
     * Do the action to add restrictions.
     *
     * @param _ The context, used for dispatching.
     * @param data The restrictions to add, the current restrictions will not be removed by
     * passing an empty object.
     * @returns A promise that resolves once the restrictions have been added.
     */
    async (_, data) => {
        const user = UserStore.loggedInUser().unsafeCoerce();
        const { cgResult } = await client.p.user.restrict({ requestBody: data.restrictions });
        const newUser = user.addSession({
            restrictions: cgResult.restrictions,
            token: cgResult.accessToken,
            expiresAt: Maybe.of(cgResult.expiresAt),
        });
        commitLogin({ user: newUser });
        await eitherAsync.toPromise(utils.signal.AFTER_USER_CHANGED.send(Maybe.of(newUser)));
    }, 'addRestrictions');
    /** Remove the last restriction layer. */
    UserStore.popRestrictions = moduleBuilder.dispatch(
    /**
     * Do the action to pop a session.
     */
    async () => {
        const { oldSession, newUser } = UserStore.loggedInUser()
            .unsafeCoerce()
            .popSession()
            .unsafeCoerce();
        commitLogin({ user: newUser });
        await eitherAsync.toPromise(utils.signal.AFTER_USER_CHANGED.send(Maybe.of(newUser)));
        await unauthorizedClient.p.user.logout({ requestBody: { token: oldSession.token } });
    }, 'popRestrictions');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UserStore.loadPermissions = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    ({ state }, payload) => {
        const c = payload.client ?? client;
        return payload.tenantId.caseOf({
            Nothing: () => utils.responseWithValue(Maybe.of(getGlobalPermissions())),
            Just: tId => utils.loadApiWithMaybeForce({
                value: Maybe.fromNullable(state.permissions[tId]),
                loader: Maybe.fromNullable(state.permissionsLoaders[tId]),
                force: false,
                load: () => c.tenant.getPermissions({ tenantId: tId }).map(response => {
                    const perms = response.cgResult.hasPerms
                        ? Maybe.of(response.cgResult.perms)
                        : Maybe.empty();
                    commitTenantPermissions({
                        tenantId: tId,
                        perms,
                    });
                    return response.map(() => perms);
                }),
                addLoader: loader => {
                    utils.vueSet(state.permissionsLoaders, tId, loader);
                },
                removeLoader: () => utils.vueDelete(state.permissionsLoaders, tId),
            }),
        });
    }, 'loadPermissions');
})(UserStore || (UserStore = {}));
